import { abweisen } from "../gemeinsam/index.js";

/** Wertebereiche der Drückjagd (Fachsprache, Domänenmodell §5, Feature Files `drueckjagd/*`). */

export const WILDARTEN = ["Raubwild", "Rehwild", "Damwild", "Rotwild", "Schwarzwild"] as const;
export type Wildart = (typeof WILDARTEN)[number];

/** Zulässige Ansprachen je Wildart („Ansprache in der App“): Rot- und Damwild nach Altersklasse, sonst nur die Wildart. */
export const ANSPRACHEN: Record<Wildart, readonly string[]> = {
  Raubwild: ["Raubwild"],
  Rehwild: ["Rehwild"],
  Damwild: ["Kalb", "Schmaltier", "Alttier", "Hirsch"],
  Rotwild: ["Kalb", "Schmaltier", "Alttier", "Hirsch"],
  Schwarzwild: ["Schwarzwild"],
};

export function istWildart(w: unknown): w is Wildart {
  return typeof w === "string" && (WILDARTEN as readonly string[]).includes(w);
}

/** Personenrollen in einer Drückjagd (ADR-0016): Jagdherr ist eine Rolle des Reviers, nicht der Einteilung. */
export const DJ_ROLLEN = [
  "Jagdleiter", "Schütze", "Ansteller", "Treiber", "Treiberführer", "Durchgeh-Hundeführer", "Standschnaller",
  "Nachsuchenführer", "Nachweisprüfer", "Bergetrupp", "Wildversorgung",
] as const;
export type DjRolle = (typeof DJ_ROLLEN)[number];

/** „führt Waffe“ (F-055a): immer gesetzt bzw. vom Jagdherrn wählbar; für alle übrigen Rollen nicht wählbar. */
export const WAFFE_PFLICHT: readonly DjRolle[] = ["Schütze", "Standschnaller", "Nachsuchenführer"];
export const WAFFE_WAEHLBAR: readonly DjRolle[] = ["Jagdleiter", "Ansteller", "Durchgeh-Hundeführer", "Bergetrupp"];

export function istDjRolle(r: unknown): r is DjRolle {
  return typeof r === "string" && (DJ_ROLLEN as readonly string[]).includes(r);
}

export function sortiereDjRollen(rollen: readonly string[]): DjRolle[] {
  return [...new Set(rollen)].filter(istDjRolle).sort((a, b) => DJ_ROLLEN.indexOf(a) - DJ_ROLLEN.indexOf(b));
}

export const SICHTBARKEITEN = ["alle Stände", "nur eigener Stand", "eigener Stand und Nachbarstände"] as const;
export type Sichtbarkeit = (typeof SICHTBARKEITEN)[number];

/** Nachsuchenführer ist Hundeeinsatzart (Anforderungen §7a K79 (a)). */
export const HUNDEEINSATZARTEN = ["Durchgeh-Hundeführer", "Standschnaller", "Nachsuchenführer"] as const;

export const JAGDSCHEINARTEN = ["Jahresjagdschein", "Tagesjagdschein", "Ausländerjagdschein", "Jugendjagdschein", "Falknerjagdschein"] as const;
export const ERGEBNISSE_JAGDSCHEIN = ["gültig vorgelegt", "nicht vorgelegt", "ungültig"] as const;
export const ERGEBNISSE_SCHIESSNACHWEIS = ["vorgelegt", "nicht vorgelegt", "zu alt", "nicht erforderlich"] as const;
export const BEANSTANDUNGEN = ["nicht vorgelegt", "ungültig", "zu alt"] as const;

/** Freitext einer Angabe: getrimmt, sonst `null`. */
export function text(wert: unknown): string | null {
  return typeof wert === "string" && wert.trim() ? wert.trim() : null;
}

/** `JJJJ-MM-TT` prüfen; sonst `unzulaessige-angaben`. */
export function datum(wert: unknown, grundFehlt: string): string {
  const t = text(wert);
  if (!t) abweisen(grundFehlt);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t) || Number.isNaN(Date.parse(`${t}T00:00:00Z`))) abweisen("unzulaessige-angaben");
  return t;
}

/** Datum minus n Monate (`JJJJ-MM-TT`), am Monatsende auf den letzten Tag des Zielmonats begrenzt. */
export function minusMonate(tag: string, monate: number): string {
  const [j, m, d] = tag.split("-").map(Number) as [number, number, number];
  const ziel = new Date(Date.UTC(j, m - 1 - monate, 1));
  const letzter = new Date(Date.UTC(ziel.getUTCFullYear(), ziel.getUTCMonth() + 1, 0)).getUTCDate();
  ziel.setUTCDate(Math.min(d, letzter));
  return ziel.toISOString().slice(0, 10);
}

/** Entfernung in Metern zwischen zwei Positionen (Haversine). */
export function entfernung(a: { Breite: number; Länge: number }, b: { Breite: number; Länge: number }): number {
  const r = 6_371_000;
  const rad = (g: number) => (g * Math.PI) / 180;
  const db = rad(b.Breite - a.Breite);
  const dl = rad(b.Länge - a.Länge);
  const h = Math.sin(db / 2) ** 2 + Math.cos(rad(a.Breite)) * Math.cos(rad(b.Breite)) * Math.sin(dl / 2) ** 2;
  return 2 * r * Math.asin(Math.sqrt(h));
}
